'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod/v4';
import { forgotPasswordAction } from '@/app/(auth)/forgot-password/actions';
import Link from 'next/link';

const forgotPasswordSchema = z.object({
  email: z.string().email('Invalid email format'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export function ForgotPasswordForm() {
  const [serverError, setServerError] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    mode: 'onTouched',
  });

  async function onSubmit(data: ForgotPasswordFormData) {
    setServerError(null);
    const result = await forgotPasswordAction(data.email);
    if (result.error) {
      setServerError(result.error);
      return;
    }
    setEmailSent(true);
  }

  if (emailSent) {
    return (
      <div className="text-center">
        <p className="mb-2 text-lg font-medium" style={{ color: '#fff' }}>Check your email</p>
        <p className="mb-4 text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
          If an account exists with that email, we sent a password reset link.
          Click the link to set a new password.
        </p>
        <Link href="/login" className="text-sm">
          Back to sign in
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} onChange={() => setServerError(null)}>
      {serverError && (
        <p role="alert" className="mb-4 rounded-lg bg-red-500/10 border border-red-500/20 px-3 py-2 text-sm text-red-300">
          {serverError}
        </p>
      )}

      <p className="mb-4 text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
        Enter your email address and we&apos;ll send you a link to reset your password.
      </p>

      <div className="mb-6">
        <label htmlFor="email">Email</label>
        <input
          id="email"
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          {...register('email')}
        />
        {errors.email && (
          <p role="alert" className="mt-1.5 text-sm text-red-400">
            {errors.email.message}
          </p>
        )}
      </div>

      <button type="submit" disabled={isSubmitting}>
        {isSubmitting ? 'Sending...' : 'Send reset link'}
      </button>

      <p className="mt-5 text-center text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>
        Remember your password?{' '}
        <Link href="/login">Sign in</Link>
      </p>
    </form>
  );
}
